import { BankIdView } from "./BankIdView";
import axios from "axios";
import { createLogger } from "@888webteam/logger-core";
import { eResults, eProductPackage } from "./enums";
import { monitor } from "./monitoring/monitoring";
import { Utils } from './utils';

export class BankIdLogic {

    constructor(context, baseUrl, isMobileDevice, useSecuredFlow) {

        monitor("init");

        this._logger = createLogger("BankIdLogic");

        this.startRequest = this.startRequest.bind(this);
        this.submitBankId = this.submitBankId.bind(this);
        
        this._baseUrl = baseUrl;
        this._context = context;
        this._isMobileDevice = isMobileDevice;
        this._useSecuredFlow = useSecuredFlow;
        this._stopStatusLoop = true;
        this._statusTimer = null;

        this._onSuccessEvent = function () { };
        this._onSuccessWithExtraDataEvent = function () { };
        this._onBackEvent = function () { };
        this._onTestBankIdInsertedEvent = function () { };
        this._onErrorDisplayedEvent = function () { };
        this._openBankIdEvent = function () { };

        this._view = new BankIdView(this._context.showBackButton === true, this._useSecuredFlow);
        this._view.onSubmit(this.submitBankId);
        this._view.onTestBankIdInserted(function () {
            monitor("testBankIdInserted")
            this._onTestBankIdInsertedEvent()
        }.bind(this));

        this._view.onBack(function () {
            monitor("back");
            this._onBackEvent();
        }.bind(this));

        this._view.onErrorDisplayed(function (errCode, errMsg) {
            this._onErrorDisplayedEvent(errCode, errMsg);
        }.bind(this));
    }

    submitBankId(bankId, done) {
        monitor("submit");

        this.startRequest(bankId, 0, done);
    }

    startRequest(bankId, retryCount, done) {
        let initiateFn = this.initiateBankIdRequest.bind(this);
        if (this._useSecuredFlow) {
            initiateFn = this.initiateSecureBankIdRequest.bind(this);
        }

        initiateFn(bankId).then(function (res) {

            monitor("submitResult", { errorCode: res.errorCode, retryCount: retryCount, requestTime: res.requestTime });

            if (res.errorCode === eResults.Ok) {

                if ([eProductPackage.DefaultAndroidWrapper, eProductPackage.AndroidGPWrapper].indexOf(this._context.clientProductPackage) === -1) {                    
                    let schemUrl = `bankid:///?autostarttoken=${res.autoStartToken}&redirect=null`;

                    if (this._useSecuredFlow !== true) {
                        schemUrl += '&External=1&NativeExternal=true';
                    }

                    this._openBankIdEvent(schemUrl, res.autoStartToken);
                }
                this._stopStatusLoop = false;

                this._view.toggleQRCode(res.qrCode);

                this.startStatusCheck(res.token, 0).then(function (statusResult) {
                    if (statusResult.completed) {

                        let civicId = this._useSecuredFlow ? statusResult.civicId : bankId;
                        this._onSuccessWithExtraDataEvent(
                                    {
                                        token: statusResult.token,
                                        bankId: civicId
                                    });
                        this._onSuccessEvent(statusResult.token);

                    } else {
                        done(statusResult.errorCode);
                    }

                }.bind(this));

            } else if (res.errorCode === eResults.GeneralError && retryCount < 5) {

                if (this._stopStatusLoop === false) {
                    this._statusTimer = setTimeout(function () {
                        this.startRequest(bankId, retryCount + 1, done);
                    }.bind(this), 1000);
                }
                else {
                    done(res.errorCode);
                }

            } else {
                done(res.errorCode);
            }

        }.bind(this));
    }

    onSuccess(callback) {
        this._onSuccessEvent = callback || function () { };
        return this;
    }

    onSuccessWithExtraData(callback){
        this._onSuccessWithExtraDataEvent = callback || function () { };
        return this;
    }

    onBack(callback) {
        this._onBackEvent = callback || function () { };
        return this;
    }

    onTestBankIdInserted(callback) {
        this._onTestBankIdInsertedEvent = callback || function () { };
        return this;
    }

    onErrorDisplayed(callback) {
        this._onErrorDisplayedEvent = callback || function () { };
        return this;
    }

    openBankId(callback) {
        this._openBankIdEvent = callback || function () { };
        return this;
    }

    initiateBankIdRequest(civicId) {

        let data = {
            CivicId: civicId,
            SystemId: this._context.systemId,
            RegulationType: this._context.regulationId,
            BrandId: this._context.brandId
        };

        let start = new Date().getTime();

        return axios.post(`${this._baseUrl}api/init/`, data, { headers: { "XX-SessionId": this._context.sessionId, "Correlation": this._context.correlation } })
            .then(function (response) {

                response.data.requestTime = new Date().getTime() - start;
                return response.data;
            })
            .catch(function (error) {

                let end = new Date().getTime();

                this._logger.error("init bankid error", error);

                return {
                    errorCode: 1,
                    autoScanToken: null,
                    requestTime: end - start
                };
            }.bind(this));
    }

    initiateSecureBankIdRequest() {
        let data = {
            SystemId: this._context.systemId,
            RegulationType: this._context.regulationId,
            BrandId: this._context.brandId,
            IsMobile: this._isMobileDevice
        };

        let start = new Date().getTime();

        return axios.post(`${this._baseUrl}api/secured-init/`, data, { headers: { "XX-SessionId": this._context.sessionId, "Correlation": this._context.correlation } })
            .then(function (response) {

                response.data.requestTime = new Date().getTime() - start;
                return response.data;
            })
            .catch(function (error) {

                let end = new Date().getTime();

                this._logger.error("init bankid error", error);

                return {
                    errorCode: 1,
                    autoStartToken: null,
                    qrCode: null,
                    requestTime: end - start
                };
            }.bind(this));
    }

    startStatusCheck(token, retryCount) {

        return new Promise(function (resolve) {

            this.checkStatus(token)
                .then(function (res) {

                    monitor("statusResult", { errorCode: res.resultCode, retryCount: retryCount, requestTime: res.requestTime, error: res.error });

                    switch (res.resultCode) {

                        case eResults.GeneralError:

                            if (this._stopStatusLoop === false) {

                                if (retryCount > 5) {
                                    resolve({ completed: false, token: null, qrCode: null, autoStartToken: null, errorCode: res.resultCode, requestTime: res.requestTime });
                                } else {

                                    this._statusTimer = setTimeout(function () {

                                        this.startStatusCheck(token, retryCount + 1).then(function (res) {
                                            resolve(res);
                                        }.bind(this));

                                    }.bind(this),
                                        2000);
                                }

                            } else {
                                resolve({ completed: false, token: null, qrCode: null, autoStartToken: null, errorCode: eResults.Ok, retryCount: retryCount, requestTime: res.requestTime });
                            }

                            break;

                        case eResults.Pending:

                            if (this._stopStatusLoop === false) {
                                this._view.toggleQRCode(res.qrCode);
                                this._statusTimer = setTimeout(function () {

                                    this.startStatusCheck(token, 0).then(function (res) {
                                        resolve(res);
                                    }.bind(this));

                                }.bind(this),
                                    2000);
                            } else {
                                resolve({ completed: false, token: null, qrCode: null, autoStartToken: null, errorCode: eResults.Ok, retryCount: retryCount, requestTime: res.requestTime });
                            }

                            break;

                        case eResults.Complete:
                            this._view.toggleQRCode(null);
                            resolve({ completed: true, token: res.token, errorCode: eResults.Ok, retryCount: retryCount, civicId: res.civicId });
                            break;

                        default:
                            this._view.toggleQRCode(null);
                            resolve({ completed: false, token: null, qrCode: null, autoStartToken: null, errorCode: res.resultCode, retryCount: retryCount });
                    }

                }.bind(this))
                .catch(function (err) {
                    this._logger.error("status check error", err);
                    resolve({ completed: false, token: null, errorCode: eResults.GeneralError, retryCount: retryCount });
                }.bind(this));
        }.bind(this));
    }

    checkStatus(token) {

        let data = {
            Token: token,
            SystemId: this._context.systemId,
            BrandId: this._context.brandId
        };

        let start = new Date().getTime();

        return axios.post(`${this._baseUrl}api/status/`, data, { headers: { "XX-SessionId": this._context.sessionId, "Correlation": this._context.correlation } })
            .then(function (response) {

                response.data.requestTime = new Date().getTime() - start;

                return response.data;
            })
            .catch(function (error) {

                let end = new Date().getTime();

                this._logger.error("status check error", error);
                return {
                    resultCode: eResults.GeneralError,
                    requestTime: end - start,
                    error: typeof error.toString === "function" ? error.toString(): "-"
                };
            }.bind(this));
    }

    reset() {
        monitor("reset");
        this._stopStatusLoop = true;
        clearTimeout(this._statusTimer);
        this._view.reset();
    }

    showError(err) {
        this._view.showCustomError(err);
    }

    showGeneralError() {
        this._view.showError(eResults.GeneralError);
    }

    clearErrors() {
        this._view.hideAllErrors();
    }
}